"use client";

import { getRequest } from "@decibeltrade/sdk";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import z from "zod/v4";

import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { env } from "@/env";
import { useAuthState } from "@/hooks/use-auth-state";
import { Logger } from "@/utils/logger";
import { authenticatedRequest, AuthNotReadyError } from "@/utils/signature-auth";

const BASE_URL = env.NEXT_PUBLIC_API_URL;

const EnrollmentResponseSchema = z.object({
  enrolled: z.boolean(),
  subaccount: z.string().optional(),
  enrolledAt: z.string().optional(),
});

const EnrollResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    enrolled: z.boolean(),
    owner: z.string(),
    subaccount: z.string(),
  }),
});

type EnrollmentResponse = z.infer<typeof EnrollmentResponseSchema>;

/**
 * Tri-state enrollment signal that callers gate on. Surfaces hide vs. show
 * decisions explicitly instead of conflating "not enrolled" with "fetch
 * failed", which previously caused the Card Vault promo dialog to render for
 * already-enrolled users when the API call returned 4xx/CORS-rejected on a
 * Vercel preview.
 *
 * - `loading`     → auth or query in flight; treat as "unknown, hide promos".
 * - `enrolled`    → API confirmed user is enrolled.
 * - `not-enrolled`→ API confirmed user is NOT enrolled (or no wallet
 *   connected — they can't be enrolled without auth).
 * - `error`       → fetch failed; do NOT default to either side.
 */
export type EnrollmentStatus = "loading" | "enrolled" | "not-enrolled" | "error";

function enrollmentQueryKey(owner: string | undefined) {
  return ["cardvault-enrollment", owner] as const;
}

export function useCardvaultEnrollment() {
  const { state: authState, accountAddress } = useAuthState();
  const { getAuthHeaders, subaccountAddress } = useWriteDexSdk();
  const queryClient = useQueryClient();

  const {
    data: enrollment,
    isLoading: isQueryLoading,
    error,
  } = useQuery({
    queryKey: enrollmentQueryKey(accountAddress),
    queryFn: async (): Promise<EnrollmentResponse> => {
      const response = await getRequest({
        schema: EnrollmentResponseSchema,
        url: `${BASE_URL}/api/card-vault/enrollment/${accountAddress}`,
        options: { credentials: "include" },
      });
      return response.data;
    },
    enabled: !!accountAddress,
    staleTime: 30_000,
    // Log once on the final retry exhaustion (mirrors `markets.ts` retry
    // hook). Avoids both the per-subscriber fanout from a `useEffect(error)`
    // and the per-attempt duplicate logging from a `queryFn` try/catch.
    retry: (failureCount, err) => {
      const shouldRetry = failureCount < 3;
      if (!shouldRetry) {
        Logger.error(err, { feature: "card-vault-enrollment", accountAddress });
      }
      return shouldRetry;
    },
  });

  const status: EnrollmentStatus =
    authState === "LOADING"
      ? "loading"
      : !accountAddress
        ? "not-enrolled"
        : isQueryLoading
          ? "loading"
          : error
            ? "error"
            : enrollment?.enrolled
              ? "enrolled"
              : "not-enrolled";

  const enrollMutation = useMutation({
    mutationFn: async ({ subaccount }: { subaccount: string }) => {
      const response = await authenticatedRequest.post({
        schema: EnrollResponseSchema,
        url: `${BASE_URL}/api/card-vault/enroll`,
        body: { subaccount },
        getAuthHeaders,
        subaccountAddress,
      });
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: enrollmentQueryKey(accountAddress) });
      void queryClient.invalidateQueries({ queryKey: ["cardvault-stats"] });
    },
  });

  return {
    status,
    isEnrolled: enrollment?.enrolled ?? false,
    enrolledSubaccount: enrollment?.subaccount,
    isLoading: isQueryLoading,
    error: error?.message ?? null,
    enroll: enrollMutation.mutateAsync,
    isEnrolling: enrollMutation.isPending,
    // Soft retry message for the hydration race; wrapper already logged.
    enrollError:
      enrollMutation.error instanceof AuthNotReadyError
        ? "Authenticating, please try again in a moment."
        : (enrollMutation.error?.message ?? null),
  };
}
